import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { graphql } from '../../__generated__';
import { Customer } from '../../__generated__/graphql';
import { GET_CUSTOMER } from './useDoctorCustomer';
import { GET_CUSTOMER_IHI } from './useCustomerIHI';

const GET_CUSTOMER_BRAND = graphql(`
  query getCustomerBrandEdit($id: ID) {
    getCustomerBrand(id: $id) {
      ...customerFragment
    }
  }
`);

export const useCustomerPortalEdit = (customerId: string, closeModal: () => void) => {
  const [formSchema, setFormSchema] = useState(undefined);

  const [mutation, { loading: saving }] = useMutation(
    graphql(`
      mutation updateCustomerPortalMutation(
        $given: String
        $customerId: String
        $postcode: String
        $street: String
        $suburb: String
        $medicarenumber: String
        $medicareref: String
        $surname: String
        $mobile: String
        $email: String
        $ihi: String
        $state: String
        $gender: String
        $dob: String
        $referralCode: String
      ) {
        updateCustomerPortal(
          customerId: $customerId
          postcode: $postcode
          street: $street
          state: $state
          suburb: $suburb
          medicarenumber: $medicarenumber
          medicareref: $medicareref
          given: $given
          surname: $surname
          mobile: $mobile
          email: $email
          dob: $dob
          ihi: $ihi
          gender: $gender
          referralCode: $referralCode
        ) {
          __typename
          id
          name
          given
          brand
          doctor
          surname
          dob
          completedProfile
          prescriptionReady
          completedConsent
          email
          mobile
          gender
          address
          suburb
          state
          postcode
          medicalhistory
          medications
          medicarenumber
          medicareref
          medicareexp
          allergies
          street
          ihi
          referredByCode
        }
      }
    `),
  );

  const setupFormSchema = (customer: Customer) => {
    setFormSchema({
      fields: [
        {
          id: 'given',
          name: 'Given',
          type: 'text',
          page: 1,
          value: customer?.given,
        },
        {
          id: 'surname',
          name: 'Surname',
          type: 'text',
          page: 1,
          value: customer?.surname,
        },
        {
          id: 'dob',
          name: 'DOB',
          help: 'Format DD/MM/YYYY',
          type: 'date',
          page: 1,
          value: customer?.dob,
        },
        {
          id: 'mobile',
          name: 'Mobile',
          type: 'text',
          page: 1,
          value: customer?.mobile,
        },
        {
          id: 'email',
          name: 'eMail',
          type: 'text',
          page: 1,
          value: customer?.email,
        },
        {
          id: 'address',
          name: 'Address',
          type: 'address',
          page: 1,
          value: customer?.address,
        },
        {
          id: 'street',
          name: 'Street',
          type: 'text',
          page: 1,
          value: customer?.street,
        },
        {
          id: 'suburb',
          name: 'Suburb',
          type: 'text',
          page: 1,
          value: customer?.suburb,
        },
        {
          id: 'postcode',
          name: 'Postcode',
          type: 'text',
          page: 1,
          value: customer?.postcode,
        },
        {
          id: 'state',
          name: 'State',
          type: 'dropdown',
          options: ['ACT', 'NSW', 'QLD', 'SA', 'TAS', 'VIC', 'WA', 'NT'],
          page: 1,
          value: customer?.state,
        },
        {
          id: 'gender',
          name: 'Gender',
          type: 'options',
          values: ['Female', 'Male', 'Other'],
          page: 1,
          value: customer?.gender,
        },
        {
          id: 'medicarenumber',
          name: 'Medicare Card Number',
          type: 'text',
          page: 1,
          value: customer?.medicarenumber ?? '',
        },
        {
          id: 'medicareref',
          name: 'Medicare Card Ref',
          type: 'text',
          page: 1,
          horizontal: true,
          value: customer?.medicareref ?? '',
        },
        {
          id: 'ihi',
          name: 'IHI',
          type: 'text',
          page: 1,
          horizontal: true,
          value: customer?.ihi ?? '',
        },
        {
          id: 'referralCode',
          name: 'Referred By Code',
          type: 'text',
          page: 1,
          value: customer?.referredByCode ?? '',
        },
      ],
      validation: JSON.stringify({
        type: 'object',
        properties: {
          given: {
            type: 'string',
          },
          surname: {
            type: 'string',
          },
          email: {
            type: 'string',
            format: 'email',
          },
          dob: {
            type: 'string',
            format: 'dateau',
          },
          medicareexp: {
            type: 'string',
            format: 'medicareexpoptional',
          },
          mobile: { type: 'string', format: 'mobilenumber' },
          gender: { enum: ['Female', 'Male', 'Other'] },
          street: { type: 'string' },
          suburb: { type: 'string' },
          state: { type: 'string' },
          ihi: { type: 'string' },
          postcode: { type: 'string' },
          medicarenumber: { type: 'string' },
          medicareref: { type: 'string' },
          referralCode: { type: 'string' },
        },
        required: ['given', 'surname', 'dob', 'mobile'],
        additionalProperties: true,
      }),
    });
  };

  const { loading } = useQuery(GET_CUSTOMER_BRAND, {
    fetchPolicy: 'network-only',
    variables: {
      id: customerId,
    },
    onCompleted: (data) => {
      setupFormSchema(data?.getCustomerBrand as Customer);
    },
  });

  const save = async (_: any, object: any) => {
    await mutation({
      variables: {
        customerId: customerId,
        ...object,
      },
      refetchQueries: [
        {
          query: GET_CUSTOMER,
          variables: { id: customerId },
          fetchPolicy: 'network-only',
        },
        {
          query: GET_CUSTOMER_IHI,
          variables: { customerId: customerId },
          fetchPolicy: 'network-only',
        },
      ],
    });

    closeModal();
  };

  return {
    loading: loading && !formSchema,
    formSchema,
    saving,

    save,
  };
};
